import React, { useState } from "react";
import { createPortal } from "react-dom";
import { NODE_COLORS, NODE_ICONS } from "../../../utils/builderTokens";

const CORE_NODES = [
  { type: "message", label: "Message", description: "Send a text response" },
  {
    type: "input_capture",
    label: "Input Capture",
    description: "Ask for and validate one response",
  },
  {
    type: "whatsapp_flow",
    label: "Whats Form",
    description: "Open an imported WhatsApp Form",
  },
  {
    type: "whatsapp_flow_dynamic_options",
    label: "Dynamic Whats Form",
    description: "Resolve dependent single-select options",
  },
  {
    type: "schedule_booking",
    label: "Schedule Booking",
    description: "Present availability and hold a selected time",
  },
  {
    type: "confirm_booking",
    label: "Confirm Booking",
    description: "Convert a held selection into a Booking",
  },
  {
    type: "media",
    label: "Media",
    description: "Send an image, video, document, or audio file",
  },
  {
    type: "template",
    label: "Template",
    description: "Send a WhatsApp template",
  },
  {
    type: "button_choice",
    label: "Choice",
    description: "Interactive button list",
  },
  {
    type: "url_cta",
    label: "URL Node",
    description: "Send a button that opens a website.",
  },
  {
    type: "branch",
    label: "Condition",
    description: "Route by a captured session value",
  },
  {
    type: "decision_table",
    label: "Decision Table",
    description: "Derive values from multiple business inputs",
  },
  {
    type: "add_flow",
    label: "Add Flow",
    description: "Continue the customer into another AutoReply flow",
  },
  { type: "wait", label: "Wait", description: "Pause flow execution" },
  { type: "tag", label: "Tag", description: "Add tags to contacts" },
  { type: "set_lead_temperature", label: "Lead Temperature", description: "Set Cold, Warm or Hot at this point in the conversation" },
  { type: "cta_flow", label: "CTA Flow", description: "Launch another flow" },
  {
    type: "handover_agent",
    label: "Handover Agent",
    description: "Message, transfer to human, and end automation",
  },
];

const COMMERCE_NODES = [
  {
    type: "browse_catalogue",
    label: "Browse Catalogue",
    tooltipLabel: "Browse Catalogue",
    description: "Let customers choose a catalogue category.",
  },
  {
    type: "search_catalogue",
    label: "Search Catalogue",
    tooltipLabel: "Search Catalogue",
    description: "Find products or services.",
  },
  {
    type: "present_catalogue_results",
    label: "Present Catalogue Results",
    tooltipLabel: "Show Products",
    description: "Let customers select a catalogue result.",
  },
  {
    type: "prepare_commerce_service",
    label: "Prepare Commerce Service",
    tooltipLabel: "Prepare Commerce Service",
    description: "Prepare the booked service for Commerce review.",
  },
  {
    type: "review_and_submit",
    label: "Review & Submit",
    tooltipLabel: "Review & Submit",
    description: "Review and submit the request.",
  },
];

const iconColors = {
  emerald: "bg-emerald-50 text-emerald-600",
  blue: "bg-blue-50 text-blue-600",
  purple: "bg-purple-50 text-purple-600",
  amber: "bg-amber-50 text-amber-600",
  slate: "bg-slate-50 text-slate-600",
  cyan: "bg-cyan-50 text-cyan-600",
  green: "bg-emerald-50 text-emerald-600",
  indigo: "bg-indigo-50 text-indigo-600",
};

export const AUTO_REPLY_NODE_ICONS = Object.freeze({
  ...NODE_ICONS,
});

// Short labels come first; keep related booking and flow controls adjacent.
const CORE_TILE_ORDER = [
  "message", "media", "template", "button_choice", "input_capture", "whatsapp_flow",
  "url_cta", "branch", "wait", "tag", "set_lead_temperature", "add_flow", "cta_flow",
  "schedule_booking", "confirm_booking", "whatsapp_flow_dynamic_options", "decision_table", "handover_agent",
];
const COMMERCE_TILE_ORDER = [
  "browse_catalogue", "search_catalogue", "review_and_submit", "present_catalogue_results", "prepare_commerce_service",
];
const orderTiles = (nodes, order) => [...nodes].sort((a, b) => order.indexOf(a.type) - order.indexOf(b.type));

export const AUTO_REPLY_PALETTE_GROUPS = Object.freeze([
  { id: "core", label: "Core", nodes: orderTiles(CORE_NODES, CORE_TILE_ORDER) },
  { id: "commerce", label: "Commerce", nodes: orderTiles(COMMERCE_NODES, COMMERCE_TILE_ORDER) },
]);

export default function AutoReplySidebar({ onAddNode }) {
  const [draggingNodeType, setDraggingNodeType] = useState("");
  const [tooltip, setTooltip] = useState(null);

  const onDragStart = (event, nodeType) => {
    setDraggingNodeType(nodeType);
    event.dataTransfer.setData("application/reactflow", nodeType);
    event.dataTransfer.effectAllowed = "move";
  };

  const showTooltip = (event, node) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltip({
      node,
      left: rect.right + 12,
      top: rect.top + rect.height / 2,
    });
  };

  return (
    <>
      <aside
        aria-label="AutoReply node palette"
        onScroll={() => setTooltip(null)}
        className="h-full min-h-0 w-full overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 font-['Inter',ui-sans-serif,system-ui,sans-serif] shadow-sm"
      >
        <div>
        {AUTO_REPLY_PALETTE_GROUPS.map((group, groupIndex) => (
          <section
            key={group.id}
            aria-label={`${group.label} nodes`}
            className={
              groupIndex
                ? "border-t border-slate-200"
                : ""
            }
          >
            {group.id !== "core" && <h2 className="sticky top-0 z-10 border-b border-slate-200 bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700">
              {group.label}
            </h2>}
            <div
              data-testid={`${group.id}-node-grid`}
              className="grid grid-cols-2 px-2 pb-2"
            >
              {group.nodes.map(node => {
                const Icon =
                  AUTO_REPLY_NODE_ICONS[node.type] ||
                  AUTO_REPLY_NODE_ICONS.default;
                const colorKey = NODE_COLORS[node.type] || NODE_COLORS.default;
                const isDragging = draggingNodeType === node.type;

                return (
                  <div key={node.type} className="min-w-0 border-b border-slate-200 py-1 odd:border-r">
                  <button
                    type="button"
                    data-node-type={node.type}
                    data-testid={`node-tile-${node.type}`}
                    aria-label={`Add ${node.label}`}
                    draggable
                    onMouseEnter={event => showTooltip(event, node)}
                    onMouseLeave={() => setTooltip(null)}
                    onFocus={event => showTooltip(event, node)}
                    onBlur={() => setTooltip(null)}
                    onDragStart={event => {
                      setTooltip(null);
                      onDragStart(event, node.type);
                    }}
                    onDragEnd={() => setDraggingNodeType("")}
                    onClick={() => onAddNode?.(node.type)}
                    className={`group relative flex h-full min-h-[60px] w-full min-w-0 cursor-grab flex-col items-center justify-start gap-1 rounded-md border px-1.5 py-1 text-center transition-[border-color,background-color,box-shadow] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 active:cursor-grabbing active:border-emerald-500 active:bg-emerald-50 ${
                      isDragging
                        ? "border-emerald-500 bg-emerald-50 hover:bg-emerald-50"
                        : "border-transparent bg-transparent hover:bg-slate-200"
                    }`}
                  >
                    <span
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${
                        iconColors[colorKey] || iconColors.slate
                      }`}
                    >
                      <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden="true" />
                    </span>
                    <span className="w-full min-w-0 break-words pt-1 text-xs font-normal leading-4 text-slate-700">{node.label}</span>
                  </button>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
        </div>
      </aside>
      {tooltip && typeof document !== "undefined"
        ? createPortal(
            <div
              role="tooltip"
              className="pointer-events-none fixed z-[1000] min-w-[190px] -translate-y-1/2 text-left"
              style={{ left: tooltip.left, top: tooltip.top }}
            >
              <div className="relative flex flex-col rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-800 shadow-lg">
                <span className="text-xs font-semibold">
                  {tooltip.node.tooltipLabel || tooltip.node.label}
                </span>
                <span className="mt-0.5 text-[11px] leading-4 text-slate-500">
                  {tooltip.node.description}
                </span>
                <span className="absolute -left-1 top-1/2 h-2.5 w-2.5 -translate-y-1/2 rotate-45 border-b border-l border-slate-200 bg-white" />
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
